<template>
  <nav class="breadcrumbs category-breadcrumbs">
    <pu-link
      v-bind="rootBreadcrumb"
      :emit-events="true"
      @url-generated="(url) => updateRenderedUrl('root', url)"
      >{{ category.breadcrumbName }}</pu-link
    ><span class="crumb">&rsaquo;</span>

    <pu-link
      v-bind="categoryCountryBreadcrumb.to"
      :disabled="!countryLinkable"
      :emit-events="true"
      @url-generated="(url) => updateRenderedUrl('country', url)"
    >
      <translate
        :tt="{
          categoryName: category.breadcrumbName,
          hierarchyName: country.name,
        }"
      >
        {categoryName} in {hierarchyName}
      </translate>
    </pu-link>
    <span class="crumb">&rsaquo;</span>

    <pu-link
      v-bind="categoryCountyBreadcrumb.to"
      :disabled="!countyLinkable"
      :emit-events="true"
      @url-generated="(url) => updateRenderedUrl('county', url)"
    >
      <translate
        :tt="{
          categoryName: category.breadcrumbName,
          hierarchyName: county.name,
        }"
      >
        {categoryName} in {hierarchyName}
      </translate>
    </pu-link>

    <nuxt-to-meta :nuxt2meta="head" />
  </nav>
</template>

<script setup lang="ts">
import { useI18n } from '~/composables/useI18n'
import { computed, onUnmounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { routeToFilters } from '~/apps/pu-links/infrastructure/routeToFilters'
import type { BreadcrumbCounts as _BreadcrumbCounts } from '~/composables/useBreadcrumbCounts'
import { useBreadcrumbCounts } from '~/composables/useBreadcrumbCounts'
import type { HierarchyPart } from '../../apps/pu-links/breadcrumbs/hierarchy-breadcrumbs'
import { SearchLinkGenerator } from '../../apps/url/generators/SearchLinkGenerator'

const props = withDefaults(
  defineProps<{
    hierarchyBreadcrumbs: HierarchyPart[]
    category: { id: string; rootSlug: string; breadcrumbName: string }
    hierarchyPath: string
    withCounts?: boolean
    campsiteDetailUrl: string
    campsiteName: string
  }>(),
  {
    withCounts: true,
  },
)

const route = useRoute()
const { t } = useI18n()
const isMounted = ref(true)

const searchLinkGenerator = new SearchLinkGenerator()

onUnmounted(() => {
  isMounted.value = false
})

const searchFilters = computed(() => routeToFilters(route))

const modifiedFilters = computed(() => ({
  ...searchFilters.value,
  facets: [],
  categoryIds: [],
  sort: undefined,
}))

const breadcrumbCountsSearchFilters = computed(() => ({
  ...modifiedFilters.value,
  hierarchyPath: props.hierarchyPath,
  facets: [],
  categoryIds:
    props.category.id === 'glamping' ? ['7', '13'] : [props.category.id],
}))

const country = computed(() => props.hierarchyBreadcrumbs[0])
const county = computed(
  () => props.hierarchyBreadcrumbs[props.hierarchyBreadcrumbs.length - 1],
)

const rootBreadcrumb = computed(() =>
  searchLinkGenerator.getUrlData({
    filters: modifiedFilters.value,
    category: props.category.rootSlug,
    customQueryParams: { pitchtype: undefined },
  }),
)

const categoryCountryBreadcrumb = computed(() => ({
  to: searchLinkGenerator.getUrlData({
    category: props.category.rootSlug,
    filters: modifiedFilters.value,
    hierarchyPath: country.value.path,
    customQueryParams: { pitchtype: undefined },
  }),
}))

const categoryCountyBreadcrumb = computed(() => ({
  to: searchLinkGenerator.getUrlData({
    category: props.category.rootSlug,
    filters: modifiedFilters.value,
    hierarchyPath: county.value.path,
    customQueryParams: { pitchtype: undefined },
  }),
}))

const { breadcrumbCounts, hasFacets } = useBreadcrumbCounts(
  breadcrumbCountsSearchFilters,
)

const hierarchyCounts = computed<Record<string, number>>(() => {
  const counts = breadcrumbCounts.value
  return counts?.bookableFacetHierarchy ?? {}
})

const renderedUrls = ref(new Map<string, string>())

const updateRenderedUrl = (key: string, url: string) => {
  if (!isMounted.value) return
  renderedUrls.value.set(key, url)
}

interface BreadcrumbItem {
  '@type': 'ListItem'
  position: number
  item: {
    '@id': string
    name: string
  }
}

const generateBreadcrumbItems = () => {
  const items: BreadcrumbItem[] = []

  // Only add root if we have its URL and it's always linkable
  if (renderedUrls.value.has('root')) {
    items.push({
      '@type': 'ListItem',
      position: items.length + 1,
      item: {
        '@id': renderedUrls.value.get('root') ?? '',
        name: props.category.breadcrumbName,
      },
    })
  }

  // Only add country if we have its URL and it's linkable
  if (renderedUrls.value.has('country') && countryLinkable.value) {
    items.push({
      '@type': 'ListItem',
      position: items.length + 1,
      item: {
        '@id': renderedUrls.value.get('country') ?? '',
        name: t('{categoryName} in {hierarchyName}', {
          categoryName: props.category.breadcrumbName,
          hierarchyName: country.value.name,
        }),
      },
    })
  }

  // Only add county if we have its URL and it's linkable
  if (renderedUrls.value.has('county') && countyLinkable.value) {
    items.push({
      '@type': 'ListItem',
      position: items.length + 1,
      item: {
        '@id': renderedUrls.value.get('county') ?? '',
        name: t('{categoryName} in {hierarchyName}', {
          categoryName: props.category.breadcrumbName,
          hierarchyName: county.value.name,
        }),
      },
    })
  }

  if (props.campsiteDetailUrl && props.campsiteName) {
    items.push({
      '@type': 'ListItem',
      position: items.length + 1,
      item: {
        '@id': props.campsiteDetailUrl,
        name: props.campsiteName,
      },
    })
  }

  return items
}

const countryLinkable = computed(() => {
  const hierarchyCount = country.value.path
    ? hierarchyCounts.value[country.value.path]
    : undefined
  const countLevel = hasFacets.value ? 5 : 1
  return hierarchyCount && hierarchyCount >= countLevel
})

const countyLinkable = computed(() => {
  const hierarchyCount = county.value.path
    ? hierarchyCounts.value[county.value.path]
    : undefined
  const countLevel = hasFacets.value ? 5 : 1
  return hierarchyCount && hierarchyCount >= countLevel
})

const head = computed(() => ({
  script: [
    {
      hid: `ldjson-schema-campsite-detail-breadcrumblist-${props.category.rootSlug}`,
      type: 'application/ld+json',
      body: true,
      json: {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: generateBreadcrumbItems(),
      },
    },
  ],
}))
</script>

<style>
@import url('@/assets/css/breadcrumbs.css');
</style>
